import { useCallback, useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { exposureView, streamExposureSnapshots } from '../lib/marketExposure/client.js'

export default function useExposureGuidance({continuous=false}={}) {
  const { user } = useAuth()
  const [version, setVersion] = useState(0)
  const [state, setState] = useState({ userId: null, snapshot: null, loading: true, failed: false })
  const [now, setNow] = useState(() => new Date())
  const refresh = useCallback(() => setVersion(value => value + 1), [])
  useEffect(() => {
    const userId = user?.id || null
    let disposed = false, controller, reconnect, failures = 0
    setState(previous => previous.userId===userId&&userId
      ? {...previous,loading:true,failed:false}
      : { userId, snapshot:null, loading:!!userId, failed:!userId })
    const connect = async () => {
      if (disposed || !userId || controller || (!continuous && document.hidden)) return
      clearTimeout(reconnect)
      controller = new AbortController()
      const timeout = setTimeout(() => controller?.abort(), 28000)
      try {
        await streamExposureSnapshots({ signal: controller.signal, onSnapshot: snapshot => {
          if (disposed) return
          failures = 0; setNow(new Date())
          setState({ userId, loading: false, failed: false, snapshot })
        } })
      } catch {
        if (!disposed && (continuous || !document.hidden)) {
          failures++
          setState(previous => ({ ...previous, loading: false, failed: true }))
        }
      } finally {
        clearTimeout(timeout); controller = null
        if (!disposed && (continuous || !document.hidden)) reconnect = setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(failures, 5)))
      }
    }
    const visibility = () => { if (continuous) { connect(); return } if (document.hidden) { clearTimeout(reconnect); controller?.abort() } else connect() }
    connect()
    const clock = setInterval(() => setNow(new Date()), 15000)
    document.addEventListener('visibilitychange', visibility)
    window.addEventListener('online', connect)
    return () => { disposed = true; controller?.abort(); clearTimeout(reconnect); clearInterval(clock); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('online', connect) }
  }, [user?.id, version, continuous])
  if (state.userId !== (user?.id || null)) return { snapshot: null, loading: true, failed: false, refresh }
  return { ...state, now, snapshot: exposureView(state.snapshot, { now, failed: state.failed || state.loading }), refresh }
}
